import {
  View,
  TextInput,
  type TextInputProps,
  type NativeSyntheticEvent,
  type TextInputFocusEventData,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import React, { useCallback, useMemo, useState } from 'react';
import colors from '~/assets/themes/colors';
import {
  MaskService,
  type TextInputMaskTypeProp,
} from 'react-native-masked-text';
import { Controller, type Control } from 'react-hook-form';
import Text from '../Text';
import InputFeedback from '../InputFeedback';
import EyeOpenSVG from '~/assets/svgs/eyeOpen.svg';
import EyeClosedSVG from '~/assets/svgs/eyeClosed.svg';

interface MaskConfigSimple {
  type: Exclude<TextInputMaskTypeProp, 'custom'>;
}

interface MaskConfigCustom {
  type: 'custom';
  mask: string;
}

interface Props extends TextInputProps {
  name: string;
  control: Control<any, any>;
  label: string;
  error?: string;
  maskConfig?: MaskConfigSimple | MaskConfigCustom;
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  isPassword?: boolean;
}

const Input = ({
  name,
  control,
  label,
  error,
  maskConfig,
  className,
  onBlur,
  onChangeText,
  onFocus,
  loading = false,
  disabled = false,
  isPassword,
  ...rest
}: Props) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hidePassword, setHidePassword] = useState(!isPassword);

  const inputStyle = useMemo(() => {
    let style = `flex-row items-center border-[1.5px] ${
      isFocused ? 'border-primary-600' : 'border-secondary-400'
    } rounded-lg color-secondary-700 pr-2 hover:border-primary-300`;

    if (disabled) {
      style += ' bg-gray-50 focus:border-secondary-400';
    }

    if (error) {
      style +=
        ' bg-feedback-error-bkg placeholder-red-900 border-[2px] hover:bg-error-100';
    } else {
      style += ` mb-[27px]`;
    }

    return style;
  }, [disabled, error, isFocused]);

  const RenderPasswordEye = useCallback(() => {
    if (hidePassword) {
      return <EyeClosedSVG />;
    }

    return (
      <View className="mt-[2px]">
        <EyeOpenSVG />
      </View>
    );
  }, [hidePassword]);

  const handleChangeText = useCallback(
    (text: string) => {
      if (maskConfig) {
        const value = MaskService.toMask(
          maskConfig.type,
          text,
          maskConfig.type === 'custom' ? { mask: maskConfig.mask } : undefined
        );

        return value;
      }

      return text;
    },
    [maskConfig]
  );

  return (
    <View className={className}>
      <Text className="mb-2 text-xs text-gray-400">{label}</Text>

      <Controller
        name={name}
        control={control}
        render={({
          field: { onChange, onBlur: onBlurHookForm, value, ...restField },
        }) => {
          const handleBlur = (
            e: NativeSyntheticEvent<TextInputFocusEventData>
          ) => {
            onBlurHookForm();

            setIsFocused(false);

            if (onBlur) {
              onBlur(e);
            }
          };

          const handleChange = (text: string) => {
            onChange(handleChangeText(text));

            if (onChangeText) {
              onChangeText(text);
            }
          };

          return (
            <View className={inputStyle}>
              <TextInput
                className={`p-4 color-secondary-700 w-full outline-none ${
                  value ? 'font-medium' : ''
                }`}
                autoCapitalize="characters"
                editable={!disabled}
                style={{
                  borderColor: error
                    ? colors.feedback.error.main
                    : colors.secondary[400],
                }}
                placeholderTextColor={
                  error ? colors.feedback.error.text : colors.gray[400]
                }
                onChangeText={handleChange}
                // onChangeText={(text) => onChange(handleChangeText(text))}
                value={value}
                onBlur={handleBlur}
                secureTextEntry={!hidePassword}
                onFocus={(e) => {
                  setIsFocused(true);
                  onFocus?.(e);
                }}
                {...rest}
                {...restField}
              />
              {loading && (
                <View className="absolute right-[0px] top-[0px] bottom-[0px] justify-center p-[5px]">
                  <ActivityIndicator color={colors.primary[600]} />
                </View>
              )}

              {isPassword && (
                <TouchableOpacity
                  className="absolute right-[10px] top-[12px] p-[5px]"
                  onPress={() => setHidePassword((prev) => !prev)}
                >
                  <RenderPasswordEye />
                </TouchableOpacity>
              )}
            </View>
          );
        }}
      />

      {!!error && <InputFeedback message={error} />}
    </View>
  );
};

export default Input;
